import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  type TaskStatus,
  fetchTaskDetail,
  updateMyTaskStatus,
} from "./api";
import { CustomSelect } from "@/components/ui/CustomSelect";

export function TaskDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("es") ? "es-ES" : "en-US";
  const navigate = useNavigate();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["task-detail", id],
    queryFn: () => fetchTaskDetail(id),
    enabled: !!id,
  });

  const [draftStatus, setDraftStatus] = useState<TaskStatus>("TODO");
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (query.data) setDraftStatus(query.data.my_status);
  }, [query.data?.my_status]);

  const mutation = useMutation({
    mutationFn: (s: TaskStatus) => updateMyTaskStatus(id, s),
    onSuccess: (data) => {
      qc.setQueryData(["task-detail", id], data);
      qc.invalidateQueries({ queryKey: ["tasks-my"] });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
    },
    onError: () => setError(t("tasks.detail.error_save")),
  });

  if (query.isLoading) {
    return (
      <div className="tasks-root">
        <p>{t("common.loading", { defaultValue: "Cargando…" })}</p>
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <div className="tasks-root">
        <p className="flash flash-error">{t("tasks.detail.error_load")}</p>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigate("/tasks/my")}
        >
          {t("tasks.detail.back")}
        </button>
      </div>
    );
  }

  const task = query.data;

  return (
    <div className="tasks-root">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "0.75rem",
        }}
      >
        <div>
          <h1 className="page-title" style={{ marginBottom: "0.25rem" }}>
            {t("nav.profile", { defaultValue: "" }) ? "" : ""}
            {task.title}
          </h1>
          <p className="page-subtitle" style={{ marginTop: 0 }}>
            {t("tasks.detail.subtitle")}
          </p>
        </div>
        <div>
          <Link to="/tasks/my" className="btn btn-secondary btn-sm">
            {t("tasks.detail.back")}
          </Link>
        </div>
      </div>

      <div className="card" style={{ marginTop: "1rem" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
          <div style={{ minWidth: 220 }}>
            <div className="summary-card-label">{t("tasks.detail.priority")}</div>
            <div>
              <span
                className={"priority-chip priority-" + task.priority.toLowerCase()}
              >
                {t(`tasks.priority_label.${task.priority}`)}
              </span>
            </div>
          </div>
          <div style={{ minWidth: 220 }}>
            <div className="summary-card-label">{t("tasks.detail.start")}</div>
            <div style={{ fontWeight: 600 }}>
              {formatDateTime(task.start_date, locale)}
            </div>
          </div>
          <div style={{ minWidth: 220 }}>
            <div className="summary-card-label">{t("tasks.detail.end")}</div>
            <div style={{ fontWeight: 600 }}>
              {formatDateTime(task.due_date, locale)}
            </div>
          </div>
          <div style={{ minWidth: 220 }}>
            <div className="summary-card-label">{t("tasks.detail.my_status")}</div>
            <div>
              <span className={"status-chip " + statusClass(task.my_status)}>
                {t(`tasks.status_label.${task.my_status}`)}
              </span>
            </div>
          </div>
        </div>

        {task.description && (
          <>
            <hr
              style={{
                margin: "1rem 0",
                border: 0,
                borderTop: "1px solid rgba(0,0,0,0.08)",
              }}
            />
            <div>
              <div className="summary-card-label">
                {t("tasks.detail.description")}
              </div>
              <div style={{ whiteSpace: "pre-wrap" }}>{task.description}</div>
            </div>
          </>
        )}
      </div>

      <div className="card" style={{ marginTop: "1rem" }}>
        <h2 className="section-title">{t("tasks.detail.update_status")}</h2>
        {savedFlash && (
          <p className="flash flash-success">{t("tasks.detail.saved")}</p>
        )}
        {error && <p className="flash flash-error">{error}</p>}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            mutation.mutate(draftStatus);
          }}
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.75rem",
            alignItems: "flex-end",
          }}
        >
          <div style={{ minWidth: 240 }}>
            <label htmlFor="my_status" className="form-label">
              {t("tasks.status")}
            </label>
            <CustomSelect
              value={draftStatus}
              options={task.all_statuses.map((s) => ({ value: s, label: t(`tasks.status_label.${s}`) }))}
              onChange={(v) => setDraftStatus(v as TaskStatus)}
              allowEmpty={false}
            />
          </div>
          <div>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={mutation.isPending || draftStatus === task.my_status}
            >
              {mutation.isPending
                ? t("tasks.detail.saving")
                : t("tasks.detail.save")}
            </button>
          </div>
        </form>
      </div>

      <div className="card" style={{ marginTop: "1rem" }}>
        <h2 className="section-title">{t("tasks.detail.assignees")}</h2>
        {task.assignees.length === 0 ? (
          <p className="page-subtitle" style={{ margin: 0 }}>
            {t("tasks.detail.no_assignees")}
          </p>
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("tasks.detail.col.user")}</th>
                  <th>{t("tasks.detail.col.status")}</th>
                  <th>{t("tasks.detail.col.completed")}</th>
                </tr>
              </thead>
              <tbody>
                {task.assignees.map((a) => (
                  <tr key={a.user_id || (a.email || "?") }>
                    <td>
                      {a.name} ({a.email})
                    </td>
                    <td>
                      <span className={"status-chip " + statusClass(a.status)}>
                        {t(`tasks.status_label.${a.status}`)}
                      </span>
                    </td>
                    <td>{formatDateTime(a.completed_at, locale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function statusClass(s: TaskStatus): string {
  return "status-" + s.toLowerCase().replace("_", "-");
}

function formatDateTime(iso: string | null, locale: string): string {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}
